import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, ChevronLeft, ChevronRight, RefreshCw } from 'lucide-react';
import { Document, Page, pdfjs } from 'react-pdf';
import { API_URL } from '../utils/apiConfig';
import { getTextInRegion } from '../api/documents';

// Ensure PDF.js worker is set if not configured globally
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const TextRegionExtractorPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [isSelecting, setIsSelecting] = useState(false);
  const [selection, setSelection] = useState(null); // { x, y, width, height }
  const [selectionStart, setSelectionStart] = useState(null);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);
  const overlayRef = useRef(null);

  const token = useMemo(() => localStorage.getItem('token'), []);

  const fileSource = useMemo(() => ({
    url: `${API_URL}/documents/${id}/file?skipEmbed=true` ,
    httpHeaders: token ? { Authorization: `Bearer ${token}` } : {}
  }), [id, token]);

  const onDocumentLoadSuccess = ({ numPages: pages }) => {
    setNumPages(pages);
    setPdfError(null);
  };

  const onDocumentLoadError = (err) => {
    setPdfError(err?.message || 'Failed to load PDF');
  };

  const getRelativePoint = (evt) => {
    const rect = overlayRef.current.getBoundingClientRect();
    const x = evt.clientX - rect.left;
    const y = evt.clientY - rect.top;
    // Clamp to bounds
    return {
      x: Math.max(0, Math.min(rect.width, x)),
      y: Math.max(0, Math.min(rect.height, y))
    };
  };

  const beginSelection = (evt) => {
    if (!overlayRef.current) return;
    const pt = getRelativePoint(evt);
    setIsSelecting(true);
    setSelectionStart(pt);
    setSelection({ x: pt.x, y: pt.y, width: 0, height: 0 });
    setResult(null);
  };

  const updateSelection = (evt) => {
    if (!isSelecting || !selectionStart || !overlayRef.current) return;
    const pt = getRelativePoint(evt);
    const x = Math.min(selectionStart.x, pt.x);
    const y = Math.min(selectionStart.y, pt.y);
    const width = Math.abs(pt.x - selectionStart.x);
    const height = Math.abs(pt.y - selectionStart.y);
    setSelection({ x, y, width, height });
  };

  const endSelection = async () => {
    if (!isSelecting) return;
    setIsSelecting(false);
    if (!selection || selection.width < 2 || selection.height < 2) return;

    try {
      setLoading(true);
      const response = await getTextInRegion(id, {
        page: pageNumber,
        x: Math.round(selection.x),
        y: Math.round(selection.y),
        width: Math.round(selection.width),
        height: Math.round(selection.height),
        tolerance: 3,
        options: { method: 'pdfjs', fallbackToTesseract: true }
      });
      setResult(response.data);
    } catch (error) {
      setResult({ error: error?.response?.data?.error || 'Failed to extract text' });
    } finally {
      setLoading(false);
    }
  };

  const clearSelection = () => {
    setSelection(null);
    setResult(null);
  };

  const goPrev = () => setPageNumber(prev => Math.max(1, prev - 1));
  const goNext = () => setPageNumber(prev => Math.min(numPages || prev + 1, prev + 1));

  useEffect(() => {
    // Clear selection and results when page changes
    setSelection(null);
    setResult(null);
  }, [pageNumber]);

  return (
    <div className="min-h-screen bg-gray-900 text-white">
      <div className="max-w-7xl mx-auto px-4 py-4">
        <div className="flex items-center gap-3 mb-4">
          <button
            className="inline-flex items-center gap-2 text-sm text-gray-300 hover:text-white"
            onClick={() => navigate(`/document/${id}`)}
          >
            <ArrowLeft size={18} /> Back to Document
          </button>
          <div className="ml-auto flex items-center gap-3">
            <button
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-50"
              onClick={goPrev}
              disabled={!numPages || pageNumber <= 1}
            >
              <ChevronLeft size={18} /> Prev
            </button>
            <div className="text-sm text-gray-300">
              Page {pageNumber}{numPages ? ` / ${numPages}` : ''}
            </div>
            <button
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-gray-800 hover:bg-gray-700 disabled:opacity-50"
              onClick={goNext}
              disabled={!numPages || pageNumber >= numPages}
            >
              Next <ChevronRight size={18} />
            </button>
            <button
              className="inline-flex items-center gap-2 px-3 py-1.5 rounded bg-gray-800 hover:bg-gray-700"
              onClick={clearSelection}
            >
              <RefreshCw size={16} /> Clear
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="bg-gray-800 rounded shadow overflow-auto p-4">
              <Document
                file={fileSource}
                onLoadSuccess={onDocumentLoadSuccess}
                onLoadError={onDocumentLoadError}
                loading={<div className="text-gray-400">Loading PDF…</div>}
                error={<div className="text-red-400">{pdfError || 'Failed to load PDF'}</div>}
              >
                <div className="relative inline-block">
                  <Page
                    pageNumber={pageNumber}
                    scale={1.0}
                    renderAnnotationLayer={false}
                    renderTextLayer={false}
                  />
                  {/* Selection overlay matching Page size at scale 1.0 */}
                  <div
                    ref={overlayRef}
                    className="absolute inset-0 cursor-crosshair"
                    onMouseDown={beginSelection}
                    onMouseMove={updateSelection}
                    onMouseUp={endSelection}
                    onMouseLeave={endSelection}
                    style={{
                      // Ensure overlay sits above canvas
                      zIndex: 10
                    }}
                  >
                    {selection && (
                      <div
                        className="absolute bg-blue-500/20 border border-blue-400"
                        style={{ left: selection.x, top: selection.y, width: selection.width, height: selection.height }}
                      />
                    )}
                  </div>
                </div>
              </Document>
            </div>
          </div>

          <div className="lg:col-span-1">
            <div className="bg-gray-800 rounded shadow p-4 h-full flex flex-col">
              <div className="text-sm text-gray-300 mb-2">Selected region</div>
              {selection ? (
                <div className="text-xs text-gray-300 mb-4">
                  x: {Math.round(selection.x)}, y: {Math.round(selection.y)}, w: {Math.round(selection.width)}, h: {Math.round(selection.height)}
                </div>
              ) : (
                <div className="text-xs text-gray-500 mb-4">Drag on the page to select a region</div>
              )}

              <div className="text-sm text-gray-300 mb-2">Extracted text</div>
              <div className="flex-1 overflow-auto">
                {loading && <div className="text-gray-400">Extracting…</div>}
                {!loading && result?.error && (
                  <div className="text-red-400 text-sm">{result.error}</div>
                )}
                {!loading && result && !result.error && (
                  <>
                    {Array.isArray(result.lines) && result.lines.length > 0 ? (
                      <pre className="whitespace-pre-wrap text-xs text-gray-100 bg-gray-900/40 p-3 rounded">{result.text}</pre>
                    ) : (
                      <div className="text-xs text-gray-500">No text found in the selected region.</div>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TextRegionExtractorPage;


